import React, { useState, useEffect, useMemo } from "react"
import { get, post } from "../utils/apiClient"
import toast from "react-hot-toast"
import {
  validateFaxNumberOnlyDigit,
  formatFaxNumber,
  prepareFinalFaxNumber,
  isEmpty,
} from "./../utils/common"
import { FAX_ACTION } from "./../utils/constants"
import { FaxNumberToolTip } from "./common/faxNumberToolTip"
import { CheckSquare, Folder, FilePen, X } from "lucide-react"

const PracticeList = ({
  practices,
  appLoading,
  authUser,
  selectedPractice,
  setSelectedPractice,
  tab,
  fetchPractices,
}) => {
  const [appContainerLoading, setAppContainerLoading] = useState(false)
  const [showComposePopup, setShowComposePopup] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [expanded, setExpanded] = useState({})
  const [searchText, setSearchText] = useState("")

  const isSuperadmin = authUser?.userRole === "SUPER_ADMIN"

  const toggleApp = (practice) => {
    const {
      appId,
      practiceId,
      faxNumberAttributes,
      subFolderWithCopiedFaxes,
      practiceName,
    } = practice

    if (practice.faxNumberAttributes.length === 0) {
      return
    }

    const firstAttr = faxNumberAttributes[0]
    setSelectedPractice({
      type: "practice",
      appId: firstAttr?.appId || appId,
      practiceId,
      faxNumberAttributes,
      subFolderWithCopiedFaxes,
      practiceName,
      faxNumber: firstAttr?.faxNumber,
      userId: firstAttr?.userId,
      customerKey: firstAttr?.customerKey,
    })

    const onSelectApp = practices.find(
      (practice) => practice.practiceId === practiceId,
    )

    if (onSelectApp.appConfigured === false) {
      toast.error(
        "Application configuration is missing. Navigate to Settings -> Application Configuration and insert record",
        {
          duration: 5000,
        },
      )
      return
    }
  }

  const toggleExpand = (practiceId) => {
    setExpanded((prev) => ({
      ...prev,
      [practiceId]: !prev[practiceId], // toggle this folder only
    }))
  }

  const reload = async () => {
    setSearchText("")
    setAppContainerLoading(true)
    await fetchPractices()
    setAppContainerLoading(false)
  }

  const handleSendFax = async (
    data,
    faxNumber,
    appId,
    originatingFaxNumber,
  ) => {
    setActionLoading(true)
    try {
      await post("/fax/composeFax", data, {
        appId: appId,
        faxAction: FAX_ACTION.compose,
        originatingFaxNumber,
        faxNumber,
      })

      setShowComposePopup(null)
      setActionLoading(false)
      toast.success("Fax has been sent successfully", {
        duration: 5000,
      })
    } catch (error) {
      console.error("Failed to send fax", error)

      const message = error || "An unexpected error occurred."
      toast.error(message, {
        duration: 5000,
      })
      setActionLoading(false)
    }
  }

  useEffect(() => {
    if (practices[0]) {
      if (!selectedPractice && (tab === "inbound" || tab === "outbound")) {
        const {
          appId,
          practiceId,
          faxNumberAttributes,
          subFolderWithCopiedFaxes,
          practiceName,
        } = practices[0]
        const firstAttr0 = faxNumberAttributes[0]
        setSelectedPractice({
          type: "practice",
          appId: firstAttr0?.appId || appId,
          practiceId,
          faxNumberAttributes,
          subFolderWithCopiedFaxes,
          practiceName,
          faxNumber: firstAttr0?.faxNumber,
          userId: firstAttr0?.userId,
          customerKey: firstAttr0?.customerKey,
        })
      }
    }
  }, [tab])
  // eslint-disable-line react-hooks/exhaustive-deps

  // set default practice On initial load
  useEffect(() => {
    if (practices[0] && !selectedPractice) {
      const {
        appId,
        practiceId,
        faxNumberAttributes,
        subFolderWithCopiedFaxes,
        practiceName,
      } = practices[0]
      const firstAttr1 = faxNumberAttributes[0]
      if (firstAttr1) {
        setSelectedPractice({
          type: "practice",
          appId: firstAttr1?.appId || appId,
          practiceId,
          faxNumberAttributes,
          subFolderWithCopiedFaxes,
          practiceName,
          faxNumber: firstAttr1?.faxNumber,
          userId: firstAttr1?.userId,
          customerKey: firstAttr1?.customerKey,
        })
      } else {
        const firstSubFolder = subFolderWithCopiedFaxes?.[0]
        if (firstSubFolder) {
          setSelectedPractice({
            type: "subfolder",
            ...firstSubFolder,
            practiceId,
            appId,
            practiceName,
            id: `${practiceId}_${firstSubFolder.subFolderName}`,
            faxNumberAttributes,
          })
          setExpanded((prev) => ({ ...prev, [practiceId]: true }))
        }
      }
    }
  }, [practices]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredPractices = practices?.filter((practice) => {
    return Object.entries(practice).some(([key, value]) => {
      if (key === "practiceName") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase().trim())
      }
      return false
    })
  })

  return (
    <div className={`${appLoading ? "locked" : ""}`}>
      <>
        {practices.length > 0 && (
          <div className="refresh-icon-container">
            {/* <span style={{ marginLeft: "12px", fontSize: "12px" }}>Reload</span> */}
            <div
              className="search-container"
              style={{
                marginRight: "5px",
                alignItems: "none",
                width: "100%",
                margin: "0px 0px 5px 0px",
              }}
            >
              <div
                className="search-input-wrapper full-width"
                style={{ width: "85%" }}
              >
                <input
                  type="text"
                  placeholder={`Search Practices...`}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ paddingRight: "22px" }}
                />
                <button
                  type="button"
                  onClick={() => setSearchText("")}
                  className="search-clear-btn"
                  style={{ visibility: searchText ? "visible" : "hidden" }}
                >
                  ✕
                </button>
              </div>
            </div>
            <button
              title="Click to reload files"
              className="refresh-button"
              onClick={() => {
                reload()
              }}
            >
              ↻
            </button>
          </div>
        )}
        {appContainerLoading ? (
          <div className="loading-container-fax-list">
            <div className="loading-message" style={{ fontSize: "0.8em" }}>
              Loading...
            </div>
          </div>
        ) : (
          <>
            {appLoading ? (
              <div className="loading-container-fax-list">
                <div className="loading-message" style={{ fontSize: "0.8em" }}>
                  Loading...
                </div>
              </div>
            ) : (
              <ul>
                {filteredPractices.map((practice) => (
                  <li
                    className="document-list-folder-li"
                    key={practice.practiceId}
                  >
                    <strong className="category">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        onClick={() => toggleExpand(practice.practiceId)}
                      >
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {practice.practiceId ===
                            selectedPractice?.practiceId &&
                          selectedPractice?.subFolderName === undefined ? (
                            <span className="user-icon">
                              <CheckSquare size={16} color="#16a34a" />
                            </span>
                          ) : (
                            <span className="user-icon">
                              <Folder size={16} color="#d97706" />
                            </span>
                          )}
                          <span
                            onClick={() =>
                              practice?.faxNumberAttributes.length > 0
                                ? toggleApp(practice)
                                : null
                            }
                            style={{
                              cursor:
                                practice?.faxNumberAttributes.length === 0
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            {practice.practiceName}
                          </span>{" "}
                        </span>
                        <span
                          style={{ fontSize: "18px", cursor: "pointer" }}
                          title="Compose Fax"
                          onClick={() =>
                            setShowComposePopup({
                              practiceId: practice.practiceId,
                              faxNumberAttributes: practice.faxNumberAttributes,
                              practiceName: practice?.practiceName,
                            })
                          }
                        >
                          <FilePen size={18} color="#333" />
                        </span>
                      </div>
                    </strong>

                    {expanded[practice.practiceId] && (
                      <div className="documentName">
                        <ul>
                          {practice.subFolderWithCopiedFaxes &&
                          practice.subFolderWithCopiedFaxes.length > 0 ? (
                            practice.subFolderWithCopiedFaxes.map(
                              (subFolder, i) => (
                                <li
                                  key={`${subFolder?.fax_number}_${i}`}
                                  onClick={() => {
                                    setSelectedPractice({
                                      type: "subfolder",
                                      ...subFolder,
                                      practiceId: practice.practiceId,
                                      appId: practice.appId,
                                      practiceName:
                                        practice?.practiceWithFaxes
                                          ?.practiceName,
                                      id: `${practice.practiceId}_${subFolder.subFolderName}`,
                                      faxNumberAttributes:
                                        practice?.faxNumberAttributes,
                                    })
                                  }}
                                  className={`file-item ${
                                    selectedPractice?.subFolderName ===
                                      subFolder?.subFolderName &&
                                    practice.practiceId ===
                                      selectedPractice?.practiceId
                                      ? "selected"
                                      : ""
                                  }`}
                                  title={
                                    !isSuperadmin ? subFolder?.fax_number : null
                                  }
                                >
                                  {selectedPractice?.subFolderName ===
                                    subFolder?.subFolderName &&
                                  practice.practiceId ===
                                    selectedPractice?.practiceId ? (
                                    <span className="user-icon">
                                      <CheckSquare size={16} color="#16a34a" />
                                    </span>
                                  ) : (
                                    <span className="user-icon">
                                      <Folder size={16} color="#d97706" />
                                    </span>
                                  )}
                                  {subFolder.subFolderName}{" "}
                                  {`(${subFolder?.totalCopiedFaxes})`}
                                </li>
                              ),
                            )
                          ) : (
                            <li className="no-files">
                              No subfolders available
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </>

      {showComposePopup && (
        <ComposeForm
          showComposePopup={showComposePopup}
          onSave={handleSendFax}
          actionLoading={actionLoading}
          onClose={() => {
            setShowComposePopup(null)
          }}
          selectedPractice={selectedPractice}
          faxNumberAttributes={showComposePopup?.faxNumberAttributes}
          // selectedFax={selectedFax}
          // document={document}
          authUser={authUser}
        />
      )}
    </div>
  )
}

export default PracticeList

const ComposeForm = ({
  showComposePopup,
  onClose,
  onSave,
  actionLoading,
  selectedPractice,
  selectedFax,
  //faxNumberAttributes,
  //document,
  authUser,
}) => {
  const [faxNumberAttributes, setFaxNumberAttributes] = useState([])
  const [toUserId, setToUserId] = useState(null)
  const [toName, setToName] = useState("")
  const [toCompany, setToCompany] = useState("")
  const [comment1, setComment1] = useState("")
  const [comment2, setComment2] = useState("")
  const [faxNumber, setFaxNumber] = useState("")
  const [includeCoverPage, setIncludeCoverPage] = useState(false)
  // const [fromName, setFromName] = useState("")
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedFiles, setSelectedFiles] = useState([])
  const [faxNumberLoading, setfaxNumberLoading] = useState(false)
  const [destinationFaxNumberLoading, setDestinationFaxNumberLoading] =
    useState(false)
  const [destinationfaxNumbers, setDestinationfaxNumbers] = useState([])

  const fetchDestinationFaxNumbers = async () => {
    setDestinationFaxNumberLoading(true)

    // let type = "PERSONAL"
    // let qString = ""
    // if (showComposePopup?.section === "queue" && selectedQueueOrNetId) {
    //   type = "QUEUE"
    //   qString = `?queue=${selectedQueueOrNetId}`
    // }

    try {
      const faxNumbers = await get(
        `/phonebook/phonebooksByPractice?practiceName=${encodeURIComponent(
          showComposePopup.practiceName,
        )}`,
      )

      setDestinationfaxNumbers(faxNumbers)
      setDestinationFaxNumberLoading(false)
    } catch (error) {
      console.error("Failed to fetch fetchFaxNumbers", error)
      setDestinationFaxNumberLoading(false)
    }
  }

  const fetchFaxNumbers = async () => {
    setfaxNumberLoading(true)
    try {
      const practicefolders = await get(
        `/practice/${showComposePopup?.practiceId}/faxNumbers`,
      )
      //const faxNumbers = practicefolders?.faxNumberAttributes

      // customFaxNumbers
      let faxNumbers = getFilteredFaxNumberAttributes(practicefolders)

      faxNumbers =
        faxNumbers.length > 0
          ? faxNumbers
          : practicefolders?.faxNumberAttributes
      // customFaxNumbers

      setFaxNumberAttributes(faxNumbers)
      setToUserId(faxNumbers[0]?.user_id)
      setfaxNumberLoading(false)
    } catch (error) {
      console.error("Failed to fetch fetchFaxNumbers", error)
      setfaxNumberLoading(false)
    }
  }

  function getFilteredFaxNumberAttributes(practicefolders) {
    const { faxNumberAttributes, customCoverPageFaxNumbers } = practicefolders

    // If custom list is empty, return all
    if (!customCoverPageFaxNumbers || customCoverPageFaxNumbers.length === 0) {
      return faxNumberAttributes
    }

    // Else, filter based on matching fax_number
    return faxNumberAttributes.filter((attr) =>
      customCoverPageFaxNumbers.includes(attr.fax_number),
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!faxNumber) {
      setErrorMessage("Destination fax number is required")
      return
    } else {
      const errorMessage = validateFaxNumberOnlyDigit(faxNumber?.trim())

      if (errorMessage) {
        setErrorMessage(errorMessage)
        return
      }
    }

    if (selectedFiles.length === 0) {
      setErrorMessage("Please attach a file(pdf, tiff, doc, docx) to send")
      return
    }

    if (selectedFiles.length > 25) {
      setErrorMessage("You can only upload up to 25 PDF files")
      return
    }

    const invalidFiles = selectedFiles.filter((file) => {
      const allowedTypes = [
        "application/pdf",
        "image/tiff",
        "application/msword", // .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      ]
      return !allowedTypes.includes(file.type)
    })

    if (invalidFiles.length > 0) {
      setErrorMessage("Only pdf, tiff, doc, docx files are allowed")
      return
    }

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0)
    if (totalSize > 100 * 1024 * 1024) {
      setErrorMessage("Total file size exceeds 100MB")
      return
    }

    // if (includeCoverPage && (!fromName || !subject || !message)) {
    //   setErrorMessage("All cover page fields must be filled if included.")
    //   return
    // }

    const metadata = {
      destinations: [
        {
          to_name: toName,
          to_company: toCompany,
          fax_number: prepareFinalFaxNumber(faxNumber),
        },
      ],
      include_cover_page: includeCoverPage,
      coverPageOptions: includeCoverPage
        ? {
            from_name: includeCoverPage ? authUser?.emailAddress : null,
            subject: includeCoverPage ? subject : null,
            message: includeCoverPage ? message : null,
          }
        : {},
      userId: toUserId,
      comment1,
      comment2,
    }

    const formData = new FormData()

    const blob = new Blob([JSON.stringify(metadata)], {
      type: "application/json",
    })
    formData.append("metadata", blob)

    selectedFiles.forEach((file) => {
      formData.append("documents", file) // name MUST include [] if backend expects it
    })

    const faxNumberForHeader =
      faxNumberAttributes.find(
        (faxNumberAttribute) => faxNumberAttribute?.user_id === toUserId,
      )?.fax_number || ""

    const appIdForHeader = faxNumberAttributes.find(
      (app) => app.user_id === toUserId,
    )?.app_id

    onSave(
      formData,
      faxNumberForHeader,
      appIdForHeader,
      prepareFinalFaxNumber(faxNumber),
    )
  }

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files)

    const combinedFiles = [...selectedFiles, ...files]

    setSelectedFiles(combinedFiles)
  }

  const handleRemoveFile = (indexToRemove) => {
    const newFiles = selectedFiles.filter((_, index) => index !== indexToRemove)
    setSelectedFiles(newFiles)
  }

  const handleMessageChange = (value) => {
    const lines = value.split("\n")

    const originatingFrom = faxNumberAttributes.find((f) => {
      return toUserId === f.user_id
    })

    // Keep everything after first line editable
    const editablePart = lines.slice(1).join("\n")
    let faxNumberText = formatFaxNumber(originatingFrom?.fax_number)

    setMessage(
      `${showComposePopup?.practiceName} | ${faxNumberText} (${originatingFrom?.last_name})
${editablePart}`,
    )
  }

  useEffect(() => {
    handleMessageChange(message)
  }, [toUserId, includeCoverPage])

  useEffect(() => {
    setErrorMessage("")
  }, [selectedFiles])

  useEffect(() => {
    if (showComposePopup) {
      fetchFaxNumbers()
      fetchDestinationFaxNumbers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showComposePopup])

  const optionDestinationFaxNumbers = destinationfaxNumbers.map((fax) => ({
    toName: `${fax.firstName} ${fax.lastName}`,
    toCompany: `${fax.company}`,
    value: fax.faxNumber,
    label: `${formatFaxNumber(fax.faxNumber)} | ${fax.firstName} ${
      fax.lastName
    } ${fax?.company ? `| ${fax?.company}` : ""}`,
  }))

  return (
    <div className="popup-overlay">
      <div className="popup compose-fax-popup" style={{ maxWidth: "800px" }}>
        {actionLoading || faxNumberLoading ? (
          <div className="loading" style={{ padding: "20px" }}>
            {faxNumberLoading ? `Loading ...` : `In progress ...`}
          </div>
        ) : (
          <>
            <div className="checkbox-popup-header">
              <h3 className="popup-title">
                Compose Fax - {showComposePopup?.practiceName}
              </h3>{" "}
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 24,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            <form
              className="popup-group form-container compose-fax-form"
              onSubmit={handleSubmit}
            >
              <div className="compose-fax-scroll-body">
                <div className="form-columns">
                  <div className="form-left">
                    <div className="form-group">
                      <label htmlFor="email">
                        Originating From:{" "}
                        <span style={{ color: "red" }}>*</span>
                      </label>
                      <select
                        className="select-dropdown"
                        id="toUserId"
                        onChange={(e) => setToUserId(e.target.value)}
                        //value={faxNumberAttributes[0].userId || ""}
                      >
                        {faxNumberAttributes.map((fax) => (
                          <option key={fax.fax_number} value={fax.user_id}>
                            {/* {fax.fax_number} */}
                            {`${fax.last_name}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">
                        Destination Fax Number:{" "}
                        <span style={{ color: "red" }}>*</span>
                        <FaxNumberToolTip />
                      </label>
                      {/* <input
                      type="text"
                      id="faxNumber"
                      value={faxNumber}
                      onChange={(e) => setFaxNumber(e.target.value)}
                    /> */}

                      {destinationFaxNumberLoading ? (
                        <div style={{ textAlign: "left", padding: "10px" }}>
                          Loading...
                        </div>
                      ) : (
                        <>
                          <FaxSearchInput
                            faxNumber={faxNumber}
                            setFaxNumber={setFaxNumber}
                            setToName={setToName}
                            setToCompany={setToCompany}
                            optionDestinationFaxNumbers={
                              optionDestinationFaxNumbers
                            }
                          />
                        </>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="toName">To Name:</label>
                      <input
                        type="text"
                        id="toName"
                        value={toName}
                        onChange={(e) => setToName(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="toCompany">To Company:</label>
                      <input
                        type="text"
                        id="toCompany"
                        value={toCompany}
                        onChange={(e) => setToCompany(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="comment1">Comment1:</label>
                      <input
                        type="text"
                        id="comment1"
                        value={comment1}
                        onChange={(e) => setComment1(e.target.value)}
                        maxLength={45}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="comment2">Comment2:</label>
                      <input
                        type="text"
                        id="comment2"
                        value={comment2}
                        onChange={(e) => setComment2(e.target.value)}
                        maxLength={45}
                      />
                    </div>
                  </div>

                  <div className="form-right" style={{ marginLeft: "10px" }}>
                    <div className="form-group">
                      <label htmlFor="attachments">
                        Attach Files(pdf, tif, doc, docx) (Max 25 files /
                        100MB): <span style={{ color: "red" }}>*</span>
                      </label>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {/* Hidden real input */}
                        <input
                          type="file"
                          id="customFileInput"
                          accept="application/pdf,image/tiff,.tif,.tiff,.doc,.docx"
                          multiple
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />

                        {/* Custom trigger button */}
                        <label
                          htmlFor="customFileInput"
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#E5E5E5",
                            color: "black",
                            // borderRadius: "4px",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          Choose Files
                        </label>

                        {/* Dynamic file count */}
                        <span
                          style={{
                            marginBottom: "5px",
                          }}
                        >
                          {selectedFiles.length > 0
                            ? `${selectedFiles.length} file${
                                selectedFiles.length > 1 ? "s" : ""
                              }`
                            : "No files selected"}
                        </span>
                      </div>
                      {/* {selectedFiles.length > 0 && (
                  <span>
                    {selectedFiles.length} file
                    {selectedFiles.length !== 1 ? "s" : ""} selected
                  </span>
                )} */}
                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {selectedFiles.length > 0 && (
                          <ul>
                            {selectedFiles.map((file, index) => (
                              <li
                                key={index}
                                style={{
                                  textAlign: "left",
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                {file.name} (
                                {(file.size / (1024 * 1024)).toFixed(2)} MB)
                                <X
                                  size={14}
                                  color="red"
                                  style={{
                                    cursor: "pointer",
                                    marginLeft: "10px",
                                  }}
                                  onClick={() => handleRemoveFile(index)}
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div
                      className="form-group checkbox-group"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "10px",
                      }}
                    >
                      <div>
                        <input
                          type="checkbox"
                          id="includeCoverPage"
                          checked={includeCoverPage}
                          onChange={(e) =>
                            setIncludeCoverPage(e.target.checked)
                          }
                          style={{ marginRight: "5px", width: "20px" }}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="includeCoverPage"
                          style={{
                            whiteSpace: "nowrap",
                            marginBottom: "0px",
                            cursor: "pointer",
                          }}
                        >
                          Include Cover Page
                        </label>
                      </div>
                    </div>

                    {includeCoverPage && (
                      <>
                        {/* <div className="form-group">
                          <label htmlFor="fromName">From Name:</label>
                          <input
                            type="text"
                            id="fromName"
                            value={fromName}
                            onChange={(e) => setFromName(e.target.value)}
                          />
                        </div> */}
                        <div className="form-group">
                          <label htmlFor="subject">Subject:</label>
                          <input
                            type="text"
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="message">Message:</label>
                          <textarea
                            id="message"
                            value={message}
                            //onChange={(e) => setMessage(e.target.value)}
                            onChange={(e) =>
                              handleMessageChange(e.target.value)
                            }
                            style={{
                              width: "100%",
                              minHeight: "80px",
                              resize: "vertical",
                            }}
                          ></textarea>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="compose-fax-footer">
                {errorMessage && (
                  <div className="error-message">{errorMessage}</div>
                )}

                <div className="popup-buttons">
                  <button
                    type="button"
                    className="popup-button cancel"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="popup-button save">
                    Send
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

function FaxSearchInput({
  faxNumber,
  setFaxNumber,
  setToName,
  setToCompany,
  optionDestinationFaxNumbers,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [hoverIndex, setHoverIndex] = useState(null)

  // keep local query in sync with external faxNumber
  useEffect(() => {
    const v = typeof faxNumber === "string" ? faxNumber : faxNumber?.value || ""
    setQuery(v || "")
  }, [faxNumber])

  const filteredOptions = useMemo(() => {
    if (!query) return optionDestinationFaxNumbers || []
    const q = query.toLowerCase().trim().replace(/-/g, "")
    return (optionDestinationFaxNumbers || []).filter((opt) =>
      String(
        opt.label.trim().replace(/-/g, "") ||
          opt.value.trim().replace(/-/g, "") ||
          "",
      )
        .toLowerCase()
        .includes(q),
    )
  }, [query, optionDestinationFaxNumbers])

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        id="faxNumber"
        name="faxNumber"
        type="text"
        placeholder="Enter a fax number"
        value={query}
        onChange={(e) => {
          const v = e.target.value
          setQuery(v)

          // keep same shape as react-select: { value, label }
          setFaxNumber(v || null)

          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // allow click to register before closing
          setTimeout(() => setOpen(false), 150)
        }}
        autoComplete="off" // disable browser suggestions
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: 0,
        }}
      />

      {open && filteredOptions.length > 0 && (
        <ul className="fax-search-input-ul">
          {filteredOptions.map((opt, idx) => (
            <li
              key={opt.value}
              onMouseDown={(e) => {
                e.preventDefault() // prevent blur before click
                setQuery(formatFaxNumber(opt.value))

                setFaxNumber(formatFaxNumber(opt.value))
                setToName(isEmpty(opt.toName, true) ? "" : opt.toName)
                setToCompany(isEmpty(opt.toCompany, true) ? "" : opt.toCompany)

                setOpen(false)
              }}
              onMouseEnter={() => setHoverIndex(idx)}
              onMouseLeave={() => setHoverIndex(null)}
              style={{
                padding: "7px 10px",
                marginTop: idx === 0 ? "5px" : "0px",
                cursor: "pointer",
                backgroundColor:
                  hoverIndex === idx ? "#bedaf8ff" : "transparent", // 👈 hover effect
              }}
            >
              {opt.label || opt.value}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
